import { AccountType } from 'src/utils/enums/payment.enum';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { IUser } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { isBrandAudi, isBrandDucati, safeString } from 'src/utils/helpers';
import {
    US_PAYOFF_QUOTE_VENDOR_URL_AU,
    US_PAYOFF_QUOTE_VENDOR_URL_VW,
} from 'src/config';
import './styles.scss';

interface IProps {
    accountType: string;
    account: any;
    onCancel: () => void;
    onGetPayoffQuote: () => void;
}

const ZERO = 0;

const isLeaseAccountType = (accountType: string) => {
    return (
        accountType == AccountType.OperatingLease ||
        accountType?.toLowerCase() === 'lease'
    );
};

const AccountListItem = ({
    label,
    value,
    hasTopDivider,
}: {
    label?: string;
    value?: string;
    hasTopDivider?: boolean;
}) => {
    return (
        <div className='c-description-list__group no-flex-wrap'>
            {hasTopDivider ? (
                <div className='c-description-list__group no-flex-wrap'>
                    <div className='item-divider' />
                </div>
            ) : (
                <>
                    <dt className='c-description-list__term item-label'>
                        {label}
                    </dt>
                    <dd className='c-description-list__detail'>
                        <span className='c-description-list__text bold item-value'>
                            {value}
                        </span>
                    </dd>
                </>
            )}
        </div>
    );
};

const AccountListColumn = ({
    items,
}: {
    items: Array<{
        label?: string;
        value?: string;
        hasTopDivider?: boolean;
    }>;
}) => {
    return (
        <div className='o-layout__item u-1/2 u-1/1@l'>
            <div className='c-description-list c-description-list--horizontal'>
                {items.map((item, index) => (
                    <AccountListItem
                        key={`${item.label ?? 'divider'}-${index}`}
                        label={item.label}
                        value={item.value}
                        hasTopDivider={item.hasTopDivider}
                    />
                ))}
            </div>
        </div>
    );
};

const PayoffQuoteDetailsUS = ({
    accountType,
    account,
    onCancel,
    onGetPayoffQuote,
}: IProps) => {
    const { payments, payoffQuotesAccount, payoffQuotesDetailUS } =
        useLocalization();
    const dealerBrand = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer?.brand;
    });
    const isAudiBrand = isBrandAudi(safeString(dealerBrand));
    const isDucatiBrand = isBrandDucati(safeString(dealerBrand));
    const brandClass = isAudiBrand ? 'audi' : isDucatiBrand ? 'ducati' : 'vw';

    const handleGetPayoffQuoteClick = () => {
        const vendorUrl =
            isAudiBrand || isDucatiBrand
                ? US_PAYOFF_QUOTE_VENDOR_URL_AU
                : US_PAYOFF_QUOTE_VENDOR_URL_VW;
        if (vendorUrl) {
            window.open(vendorUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        onGetPayoffQuote();
    };

    // Account type flags
    const isSinglePayLease = account?.singlePayLease;
    const isRetailAccount =
        accountType === AccountType.RetailBalloon ||
        accountType === AccountType.RetailLoan;
    const isOperatingLease =
        isLeaseAccountType(accountType) && !isSinglePayLease;

    // Derived conditions for payment details display
    const isRetailOrSinglePayLeaseAccount = isRetailAccount || isSinglePayLease;

    // Account data
    const contractDate = account?.contractDate;
    const monthlyPaymentExcludeTax = account?.scheduledPaymentAmount ?? ZERO;
    const monthlyPaymentIncludingTax =
        account?.ScheduledPeriodicPaymentAmount ??
        account?.scheduledPaymentAmount ??
        ZERO;
    const otherInvoicedCharges = account?.FeesLateCharges ?? ZERO;

    // Calculated totals
    const totalRemainingPayments =
        account?.TotalAmountOfRemainingCharges ??
        Number(monthlyPaymentExcludeTax) *
            Number(account?.PaymentRemaining ?? ZERO);
    const totalOutstandingBalance = isOperatingLease
        ? account?.currentBalance
        : account?.TotalAmount ?? ZERO;

    const typeLabelMap: Record<string, string> = {
        [AccountType.RetailLoan]: payoffQuotesDetailUS.RETAIL_LOAN,
        [AccountType.RetailBalloon]: payoffQuotesDetailUS.RETAIL_BALLOON,
    };
    const typeLabel = isLeaseAccountType(accountType)
        ? isSinglePayLease
            ? payoffQuotesDetailUS.SINGLE_PAY_LEASE
            : payoffQuotesDetailUS.OPERATING_LEASE
        : typeLabelMap[accountType] ?? accountType;

    // Payment Details Display Logic:
    // LAST_PAYMENT_DATE and TOTAL_OUTSTANDING_BALANCE shown for all account types
    // - Single Pay Lease: REMAINING_PAYMENTS (left), OTHER_INVOICED_CHARGES (right)
    // - Operating Lease: MONTHLY_PAYMENT_EXCLUDES_TAX (left), OUTSTANDING_BALANCE (right)
    // - Retail (Balloon/Loan): REMAINING_PAYMENTS + MONTHLY_PAYMENT_INCLUDES_TAX (left),
    //                          TOTAL_REMAINING_PAYMENTS + OTHER_INVOICED_CHARGES (right)

    const accountInformationLeftItems = [
        {
            label: payments.PAYMENT_INFORMATION_ACCOUNTNUMBERLABEL,
            value: account?.accountNumber ?? '-',
        },
        {
            label: payments.PAYMENT_INFORMATION_CUSTOMERNAMELABEL,
            value: account?.customerName ?? '-',
        },
        {
            label: payoffQuotesDetailUS.VIN_LABEL,
            value: account?.vin ?? '-',
        },
    ];

    const accountInformationRightItems = [
        {
            label: payoffQuotesDetailUS.ORIGINAL_CONTRACT_DATE,
            value: contractDate ? formatDate(contractDate) : '-',
        },
        {
            label: payments.PAYMENT_INFORMATION_ACCOUNTTYPELABEL,
            value: typeLabel,
        },
        {
            label: payoffQuotesAccount.TERM,
            value:
                account?.contractTerm !== undefined
                    ? String(account.contractTerm)
                    : '-',
        },
    ];

    const paymentDetailsLeftItems = [
        ...(isOperatingLease
            ? [
                  {
                      label: payoffQuotesDetailUS.MONTHLY_PAYMENT_EXCLUDES_TAX,
                      value: formatMoney(monthlyPaymentExcludeTax),
                  },
              ]
            : []),
        ...(isRetailAccount || isSinglePayLease
            ? [
                  {
                      label: payoffQuotesDetailUS.REMAINING_PAYMENTS,
                      value:
                          account?.remainingPayments !== undefined
                              ? String(account.remainingPayments)
                              : '-',
                  },
              ]
            : []),
        ...(isRetailAccount
            ? [
                  {
                      label: payoffQuotesDetailUS.MONTHLY_PAYMENT_INCLUDES_TAX,
                      value: formatMoney(monthlyPaymentIncludingTax),
                  },
              ]
            : []),
        {
            label: payoffQuotesDetailUS.LAST_PAYMENT_DATE,
            value: account?.lastPaymentDate
                ? formatDate(account.lastPaymentDate)
                : '-',
        },
    ];

    const paymentDetailsRightItems = [
        ...(isRetailAccount
            ? [
                  {
                      label: payoffQuotesDetailUS.TOTAL_REMAINING_PAYMENTS,
                      value: formatMoney(totalRemainingPayments),
                  },
              ]
            : []),
        ...(isRetailOrSinglePayLeaseAccount
            ? [
                  {
                      label: payoffQuotesDetailUS.OTHER_INVOICED_CHARGES,
                      value: formatMoney(otherInvoicedCharges),
                  },
              ]
            : []),
        ...(isOperatingLease
            ? [
                  {
                      label: payoffQuotesDetailUS.OUTSTANDING_BALANCE,
                      value: formatMoney(account?.currentBalance),
                  },
              ]
            : []),
    ];

    return (
        <>
            <div className='title-wrapper'>
                <h5>
                    <span className='c-section-heading__title-text'>
                        {payoffQuotesDetailUS.TITLE}
                    </span>
                </h5>
                <div className='action-button u-text-right'>
                    <button
                        className='c-btn--secondary c-btn c-btn--small'
                        type='button'
                        onClick={onGetPayoffQuote}
                    >
                        <span className='c-btn__text'>
                            {payoffQuotesDetailUS.NEW_SEARCH}
                        </span>
                    </button>
                </div>
            </div>
            <section className='c-form-section quote-account-information-us'>
                <div className='c-form-section__content'>
                    <div className='title'>
                        <h6>
                            <span className='c-section-heading__title-text u-mr-xxsmall section-title'>
                                {payoffQuotesDetailUS.ACCOUNT_INFO_TITLE}
                            </span>
                        </h6>
                    </div>
                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            <AccountListColumn
                                items={accountInformationLeftItems}
                            />
                            <AccountListColumn
                                items={accountInformationRightItems}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className='c-form-section quote-summary-us payment-details-us'>
                <div className='c-form-section__content'>
                    <div className='title'>
                        <h6>
                            <span className='c-section-heading__title-text u-mr-xxsmall section-title'>
                                {payoffQuotesDetailUS.PAYMENT_DETAILS_TITLE}
                            </span>
                        </h6>
                    </div>

                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            <AccountListColumn
                                items={paymentDetailsLeftItems}
                            />
                            <AccountListColumn
                                items={paymentDetailsRightItems}
                            />
                        </div>
                    </div>

                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item u-1/2 u-1/1@l total-outstanding-balance-us'>
                                <div className='c-description-list c-description-list--horizontal'>
                                    <div className='c-description-list__group no-flex-wrap'>
                                        <div className='item-divider' />
                                    </div>
                                    <div className='c-description-list__group no-flex-wrap'>
                                        <dt className='c-description-list__term item-label bold'>
                                            {
                                                payoffQuotesDetailUS.TOTAL_OUTSTANDING_BALANCE
                                            }
                                        </dt>
                                        <dd className='c-description-list__detail'>
                                            <span className='c-description-list__text bold item-value'>
                                                {formatMoney(
                                                    totalOutstandingBalance,
                                                )}
                                            </span>
                                        </dd>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='o-fieldset__row quote-information-info-us quote-footnotes-us'>
                        <div className='o-layout'>
                            <div className='o-layout__item u-1/1 quote-information-info-us__notes'>
                                <div>
                                    {payoffQuotesDetailUS.FOOTNOTE_PROJECTED}
                                </div>
                                <div>
                                    {payoffQuotesDetailUS.FOOTNOTE_DISPOSITION}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='o-fieldset__row u-mt-large quote-actions-us'>
                        <button
                            className='c-btn c-btn--secondary c-btn--small'
                            type='button'
                            onClick={onCancel}
                        >
                            <span className='c-btn__text'>
                                {payoffQuotesDetailUS.CANCEL_BUTTON}
                            </span>
                        </button>
                        <button
                            className={`c-btn c-btn--small quote-get-payoff-btn bg-${brandClass}`}
                            type='button'
                            onClick={handleGetPayoffQuoteClick}
                        >
                            <span className='c-btn__text'>
                                {payoffQuotesDetailUS.GET_PAYOFF_QUOTE_BUTTON}
                            </span>
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PayoffQuoteDetailsUS;
